import * as React from 'react';
import { useEffect, useCallback } from 'react';

import { Table, Typography, Tag, Row, Col, DatePicker, Select, Input, Space, Card, Modal, Button, Divider } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

import { ExecutionHistoryItem, ActionsHistoryItem, ActionsHistoryRequest, DictionariesResponse } from '../../types';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Компонент для изменяемого заголовка
const ResizableTitle = (props: any) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <th
      {...restProps}
      style={{
        ...restProps.style,
        position: 'relative',
        userSelect: 'none',
      }}
    >
      {restProps.children}
      <div
        style={{
          position: 'absolute',
          right: -5,
          top: 0,
          bottom: 0,
          width: 10,
          cursor: 'col-resize',
          zIndex: 1,
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          const startX = e.clientX;
          const startWidth = width;

          const handleMouseMove = (e: MouseEvent) => {
            const newWidth = Math.max(50, startWidth + e.clientX - startX);
            onResize(newWidth);
          };

          const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
          };

          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
        }}
      />
    </th>
  );
};

interface HistoryFilters {
  dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
  ruleName: string | undefined;
  objectNumber: string;
  actionTaken: string | undefined;
  actionType: string | undefined;
}

interface ExecutionHistoryTableProps {
  history: ExecutionHistoryItem[];
  apiHistory: ActionsHistoryItem[];
  loading: boolean;
  pagination: {
    current_page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
  dictionaries?: DictionariesResponse['data'];
  onFiltersChange?: (filters: ActionsHistoryRequest) => void;
  onPageChange?: (page: number, pageSize: number) => void;
}

export function ExecutionHistoryTable({ history, apiHistory, loading, pagination, dictionaries, onFiltersChange, onPageChange }: ExecutionHistoryTableProps) {
  const [modalVisible, setModalVisible] = React.useState(false);
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [selectedNumbers, setSelectedNumbers] = React.useState<string[]>([]);
  const [modalTitle, setModalTitle] = React.useState('');
  const [telegramModalVisible, setTelegramModalVisible] = React.useState(false);
  const [selectedTelegramMessage, setSelectedTelegramMessage] = React.useState('');
  const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>({
    dateTime: 130,
    ruleName: 160,
    checkType: 100,
    objectNumbers: 100,
    executedCondition: 280,
    actionType: 130,
    actionTaken: 180,
  });

  const [filters, setFilters] = React.useState<HistoryFilters>({
    dateRange: null,
    ruleName: undefined,
    objectNumber: '',
    actionTaken: undefined,
    actionType: undefined,
  });

  // Получаем уникальные названия правил для фильтра
  const uniqueRuleNames = React.useMemo(() => {
    if (dictionaries?.rule_names) {
      return dictionaries.rule_names.map(rule => ({ label: rule.name, value: rule.name }));
    }
    const names = [...new Set(history.map(item => item.ruleName))];
    return names.map(name => ({ label: name, value: name }));
  }, [history, dictionaries]);

  // Получаем уникальные действия для фильтра
  const uniqueActionTypes = React.useMemo(() => {
    if (dictionaries?.action_types) {
      const allActions = Object.values(dictionaries.action_types).flatMap(sourceActions =>
        Object.values(sourceActions).flat()
      );
      return allActions.map(action => ({ label: action.name, value: action.name }));
    }
    // Предопределенные варианты действий
    return [
      { label: 'Ничего не меняем', value: 'Ничего не меняем' },
      { label: 'Увеличиваем недельный бюджет', value: 'Увеличиваем недельный бюджет' },
      { label: 'Уменьшаем недельный бюджет', value: 'Уменьшаем недельный бюджет' },
      { label: 'Отключаем', value: 'Отключаем' },
    ];
  }, [dictionaries]);

  // Функция для преобразования локальных фильтров в API формат
  const convertFiltersToApiFormat = useCallback((localFilters: HistoryFilters): ActionsHistoryRequest => {
    const apiFilters: ActionsHistoryRequest = {};

    // Фильтр по правилам
    if (localFilters.ruleName && dictionaries?.rule_names) {
      const rule = dictionaries.rule_names.find(r => r.name === localFilters.ruleName);
      if (rule) {
        apiFilters.rule_ids = [typeof rule.id === 'string' ? parseInt(rule.id) : rule.id];
      }
    }

    // Фильтр по типам действий
    if (localFilters.actionType && dictionaries?.action_types) {
      const allActions = Object.values(dictionaries.action_types).flatMap(sourceActions =>
        Object.values(sourceActions).flat()
      );
      const action = allActions.find(a => a.name === localFilters.actionType);
      if (action) {
        apiFilters.action_type_ids = [typeof action.id === 'string' ? parseInt(action.id) : action.id];
      }
    }

    // Фильтр по дате
    if (localFilters.dateRange && localFilters.dateRange[0] && localFilters.dateRange[1]) {
      apiFilters.date_from = localFilters.dateRange[0].format('YYYY-MM-DD');
      apiFilters.date_to = localFilters.dateRange[1].format('YYYY-MM-DD');
    }

    // Фильтр по номеру объекта
    if (localFilters.objectNumber) {
      const objectNumber = parseInt(localFilters.objectNumber);
      if (!isNaN(objectNumber)) {
        apiFilters.object_number = objectNumber;
      }
    }

    return apiFilters;
  }, [dictionaries]);

  useEffect(() => {
    if (onFiltersChange && dictionaries && isInitialized) {
      const apiFilters = convertFiltersToApiFormat(filters);
      onFiltersChange(apiFilters);
    }
    // eslint-disable-next-line
  }, [filters]);

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  const displayData = apiHistory;
  const isApiData = true;

  const filteredHistory = React.useMemo(() => {
    return displayData;
  }, [displayData]);

  const handleDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    setFilters(prev => ({ ...prev, dateRange: dates }));
  };

  const handleRuleNameChange = (value: string | undefined) => {
    setFilters(prev => ({ ...prev, ruleName: value }));
  };

  const handleObjectNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, objectNumber: e.target.value }));
  };

  const handleActionTypeChange = (value: string | undefined) => {
    setFilters(prev => ({ ...prev, actionType: value }));
  };

  const showNumbersModal = (numbers: string[], ruleName: string) => {
    setSelectedNumbers(numbers);
    setModalTitle(`Номера объектов для правила "${ruleName}"`);
    setModalVisible(true);
  };

  const showTelegramModal = (message: string, ruleName: string) => {
    setSelectedTelegramMessage(message);
    setTelegramModalVisible(true);
  };

  const clearFilters = () => {
    setFilters({
      dateRange: null,
      ruleName: undefined,
      objectNumber: '',
      actionTaken: undefined,
      actionType: undefined,
    });
    if (onFiltersChange) {
      onFiltersChange({});
    }
  };

  const handleResize = (key: string) => (width: number) => {
    setColumnWidths(prev => ({ ...prev, [key]: width }));
  };

  // Создаем колонки в зависимости от типа данных
  const createColumns = (): ColumnsType<ExecutionHistoryItem | ActionsHistoryItem> => {
    if (isApiData) {
      return [
        {
          title: 'Дата и время',
          dataIndex: 'created_at',
          key: 'created_at',
          width: columnWidths.dateTime,
          onHeaderCell: () => ({
            width: columnWidths.dateTime,
            onResize: handleResize('dateTime'),
          }),
          render: (createdAt: string) => (
            <Text style={{ fontSize: 12 }}>
              {dayjs(createdAt).format('DD.MM.YYYY HH:mm')}
            </Text>
          ),
        },
        {
          title: 'Правило',
          dataIndex: 'rule_name',
          key: 'rule_name',
          width: columnWidths.ruleName,
          onHeaderCell: () => ({
            width: columnWidths.ruleName,
            onResize: handleResize('ruleName'),
          }),
          render: (ruleName: string) => (
            <Text strong style={{ fontSize: 12 }}>{ruleName}</Text>
          ),
        },
        {
          title: 'Проверяются',
          dataIndex: 'object_type_name',
          key: 'object_type_name',
          width: columnWidths.checkType,
          onHeaderCell: () => ({
            width: columnWidths.checkType,
            onResize: handleResize('checkType'),
          }),
          render: (objectTypeName: string) => (
            <Tag color="blue">{objectTypeName}</Tag>
          ),
        },
        {
          title: '№ объекта',
          dataIndex: 'objects',
          key: 'objects',
          width: columnWidths.objectNumbers,
          onHeaderCell: () => ({
            width: columnWidths.objectNumbers,
            onResize: handleResize('objectNumbers'),
          }),
          render: (objects: string) => {
            try {
              const objectsArray = typeof objects === 'string' ? JSON.parse(objects) : objects;
              const objectsList = Array.isArray(objectsArray) ? objectsArray : Object.values(objectsArray);

              if (!objectsList || objectsList.length === 0) {
                return <Text code style={{ fontSize: 12 }}>—</Text>;
              }

              if (objectsList.length === 1) {
                return <Text code style={{ fontSize: 12 }}>{objectsList[0]}</Text>;
              }

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Text code style={{ fontSize: 12 }}>{objectsList[0]}</Text>
                  <Button
                    type="link"
                    size="small"
                    style={{
                      fontSize: 11,
                      padding: '0 4px',
                      height: 'auto',
                      minHeight: 'auto',
                      lineHeight: 1,
                      alignSelf: 'flex-start'
                    }}
                    onClick={() => showNumbersModal(objectsList, 'API Rule')}
                  >
                    +{objectsList.length - 1} ещё
                  </Button>
                </div>
              );
            } catch (error) {
              return <Text code style={{ fontSize: 12 }}>—</Text>;
            }
          },
        },
        {
          title: 'Выполненное условие',
          dataIndex: 'executed_condition',
          key: 'executed_condition',
          width: columnWidths.executedCondition,
          onHeaderCell: () => ({
            width: columnWidths.executedCondition,
            onResize: handleResize('executedCondition'),
          }),
          render: (executed_condition: string) => (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {executed_condition}
            </Text>
          ),
        },
        {
          title: 'Что сделано',
          dataIndex: 'action_type_name',
          key: 'action_type_name',
          width: columnWidths.actionType,
          onHeaderCell: () => ({
            width: columnWidths.actionType,
            onResize: handleResize('actionType'),
          }),
          render: (actionTypeName: string) => {
            let color = 'default';
            if (actionTypeName.includes('Увеличиваем')) color = 'green';
            else if (actionTypeName.includes('Уменьшаем')) color = 'orange';
            else if (actionTypeName.includes('Отключаем')) color = 'red';
            else if (actionTypeName.includes('Ничего не меняем')) color = 'blue';

            return <Tag color={color}>{actionTypeName}</Tag>;
          },
        },
        {
          title: 'Детали',
          dataIndex: 'details',
          key: 'details',
          width: columnWidths.actionTaken,
          onHeaderCell: () => ({
            width: columnWidths.actionTaken,
            onResize: handleResize('actionTaken'),
          }),
          render: (details: string) => (
            <Text style={{ fontSize: 12 }}>
              {details}
            </Text>
          ),
        },
      ];
    }

    return [];
  };

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16, border: '1px solid #d9d9d9', borderRadius: '8px' }}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text strong>Фильтры</Text>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text>Период</Text>
                <RangePicker
                  value={filters.dateRange as any}
                  onChange={handleDateRangeChange as any}
                  format="DD.MM.YYYY"
                  placeholder={['Дата от', 'Дата до']}
                  style={{ width: '100%' }}
                />
              </Space>
            </Col>
            <Col span={6}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text>Правило</Text>
                <Select
                  value={filters.ruleName}
                  onChange={handleRuleNameChange}
                  placeholder="Выберите правило"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label as string).toLowerCase().includes(input.toLowerCase())
                  }
                  style={{ width: '100%' }}
                  dropdownMatchSelectWidth={false}
                  options={uniqueRuleNames}
                />
              </Space>
            </Col>
            <Col span={6}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text>№ объекта</Text>
                <Input
                  value={filters.objectNumber}
                  onChange={handleObjectNumberChange}
                  placeholder="Введите номер объекта"
                  allowClear
                />
              </Space>
            </Col>
            <Col span={6}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text>Что сделано</Text>
                <Select
                  value={filters.actionTaken}
                  onChange={handleActionTypeChange}
                  placeholder="Выберите действие"
                  allowClear
                  style={{ width: '100%' }}
                  dropdownMatchSelectWidth={false}
                  options={uniqueActionTypes}
                />
              </Space>
            </Col>
          </Row>
          {(filters.dateRange || filters.ruleName || filters.objectNumber || filters.actionTaken) && (
            <div style={{ textAlign: 'right' }}>
              <Text
                type="secondary"
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={clearFilters}
              >
                Очистить фильтры
              </Text>
            </div>
          )}
        </Space>
      </Card>

      <div style={{ border: '1px solid #d9d9d9', borderRadius: '8px', overflow: 'hidden' }}>
        <Table
          components={{
            header: {
              cell: ResizableTitle,
            },
          }}
          columns={createColumns()}
          dataSource={filteredHistory}
          rowKey={(record) => `${(record as any).rule_id}-${(record as any).created_at}`}
          loading={loading}
          pagination={isApiData ? {
            current: pagination.current_page,
            pageSize: pagination.page_size,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} записей`,
            onChange: (page, pageSize) => {
              if (onPageChange) {
                onPageChange(page, pageSize);
              }
            },
          } : {
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} записей`,
          }}
          size="small"
          bordered
        />
      </div>

      <Modal
        title={modalTitle}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Закрыть
          </Button>
        ]}
      >
        <div style={{ maxHeight: 400, overflowY: 'auto', padding: '8px 0' }}>
          {selectedNumbers.map((number, index) => (
            <div key={index} style={{ padding: '4px 8px', borderBottom: index < selectedNumbers.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
              <Text code>{number}</Text>
            </div>
          ))}
        </div>
      </Modal>

      <Modal
        title="Отправленное уведомление в Telegram"
        open={telegramModalVisible}
        onCancel={() => setTelegramModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setTelegramModalVisible(false)}>
            Закрыть
          </Button>
        ]}
        width={500}
      >
        <div style={{ padding: '8px 0' }}>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Текст сообщения:
          </Typography.Text>
          <Divider style={{ margin: '8px 0' }} />
          <div style={{
            background: '#f5f5f5',
            padding: '12px',
            borderRadius: '6px',
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            fontSize: '13px',
            lineHeight: '1.4'
          }}>
            {selectedTelegramMessage}
          </div>
        </div>
      </Modal>
    </div>
  );
}
